import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Radio, Alert, Space, Card, Tooltip } from 'antd';
import {
  CalendarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DuplicateGroup } from '../../../../types/contact';
import { formatPhone } from '../../../../utils/phone';

const FULL_FMT = 'DD/MM/YYYY HH:mm';

interface MergeContactsModalProps {
  group: DuplicateGroup;
  onClose: () => void;
  onMerge: (primaryId: string, duplicateIds: string[]) => Promise<void>;
}

export const MergeContactsModal: React.FC<MergeContactsModalProps> = ({
  group,
  onClose,
  onMerge,
}) => {
  const { t } = useTranslation();
  const [selectedPrimary, setSelectedPrimary] = useState<string>(group.contacts[0].id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    const duplicateIds = group.contacts
      .filter((c) => c.id !== selectedPrimary)
      .map((c) => c.id);

    setLoading(true);
    setError(null);
    try {
      await onMerge(selectedPrimary, duplicateIds);
    } catch (err: any) {
      setError(err.message || t('admin.contacts.error.merge'));
    } finally {
      setLoading(false);
    }
  };

  const primaryContact = group.contacts.find((c) => c.id === selectedPrimary);
  const allProducers = group.contacts.flatMap((c) => c.producers);
  const uniqueProducers = Array.from(
    new Map(allProducers.map((p) => [p.id, p])).values()
  );

  return (
    <Modal
      title={t('admin.contacts.merge.title')}
      open={true}
      onCancel={onClose}
      onOk={handleConfirm}
      okText={t('admin.contacts.merge.confirm')}
      cancelText={t('common.cancel')}
      confirmLoading={loading}
      width={800}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Alert
          message={t('admin.contacts.merge.warning')}
          type="warning"
          icon={<ExclamationCircleOutlined />}
          showIcon
        />

        {error && (
          <Alert
            message={t('common.error')}
            description={error}
            type="error"
            showIcon
          />
        )}

        <div>
          <h3>{t('admin.contacts.merge.select_primary')}</h3>
          <Radio.Group
            value={selectedPrimary}
            onChange={(e) => setSelectedPrimary(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {group.contacts.map((contact) => {
                // Diferencia >1s entre created/modified → realmente
                // editado. Si no, solo enseñamos la creación para no
                // duplicar info igual.
                const wasModified =
                  contact.created &&
                  contact.modified &&
                  !dayjs(contact.created).isSame(
                    dayjs(contact.modified),
                    'second',
                  );
                return (
                  <Radio key={contact.id} value={contact.id} style={{ width: '100%' }}>
                    <Card size="small" style={{ marginLeft: '24px' }}>
                      <div>
                        <strong>{contact.name} {contact.surname}</strong>
                      </div>
                      {contact.email && <div>{contact.email}</div>}
                      {contact.main_phone && <div>{formatPhone(contact.main_phone)}</div>}
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        {t('admin.contacts.producers')}: {contact.producers.map((p) => p.name).join(', ')}
                      </div>
                      {/* Trazabilidad: el admin necesita saber cuál
                          es el contacto más reciente o más antiguo
                          para decidir qué dejamos como primario. */}
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          color: '#8c8c8c',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0 12px',
                        }}
                      >
                        <Tooltip
                          title={
                            contact.created
                              ? dayjs(contact.created).format(FULL_FMT)
                              : undefined
                          }
                        >
                          <span>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {t('admin.contacts.created', 'Creado')}:{' '}
                            {contact.created
                              ? dayjs(contact.created).format('DD/MM/YYYY')
                              : '—'}
                          </span>
                        </Tooltip>
                        {wasModified && (
                          <Tooltip
                            title={dayjs(contact.modified).format(FULL_FMT)}
                          >
                            <span>
                              <EditOutlined style={{ marginRight: 4 }} />
                              {t('admin.contacts.modified', 'Modificado')}:{' '}
                              {dayjs(contact.modified).format('DD/MM/YYYY')}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </Card>
                  </Radio>
                );
              })}
            </Space>
          </Radio.Group>
        </div>

        <div>
          <h3>{t('admin.contacts.merge.preview')}</h3>
          <Card>
            <h4>{t('admin.contacts.merge.resulting_contact')}</h4>
            <p>
              <strong>{t('contacts.field.name')}:</strong> {primaryContact?.name} {primaryContact?.surname}
            </p>
            {primaryContact?.email && (
              <p>
                <strong>{t('contacts.field.email')}:</strong> {primaryContact.email}
              </p>
            )}
            {primaryContact?.main_phone && (
              <p>
                <strong>{t('contacts.field.phone')}:</strong> {formatPhone(primaryContact.main_phone)}
              </p>
            )}

            <h4 style={{ marginTop: '16px' }}>{t('admin.contacts.merge.associated_producers')}</h4>
            <ul>
              {uniqueProducers.map((p) => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
          </Card>
        </div>
      </Space>
    </Modal>
  );
};
